/**
 * batch-67 meldingen-ontvangers (FO-500) - het herleidbaarheidsspoor van een
 * melding: waarom kreeg deze ontvanger deze melding?
 *
 * Wordt als `Notificatie.ontvangerBron` (Json) bewaard, gevuld door de drie
 * leveringspaden (`notify()`, `triggerNotificatieEvent()`, `fireSignal()`) uit
 * de uitkomst van het interceptiepunt (`bepaalEffectieveOntvangers`). Bevat
 * uitsluitend routerings-metadata: sleutels, resolver-key, override-id, rol,
 * escalatieniveau en de postvak-functie. Nooit inhoud van de melding, nooit
 * persoonsgegevens van derden.
 *
 * Eigen bladmodule zonder imports, zodat zowel `notificaties.ts` (het lijst-DTO)
 * als `notificatie-ontvangers.ts` (het beheerscherm) hem kunnen lezen zonder
 * import-cykel. De kant-en-klare zin met de juiste labels staat in
 * `notificatie-waarom.ts` (`waaromZin`).
 */

import { z } from 'zod'

/** Hoe de ontvangerslijst van een melding tot stand kwam (interceptiepunt). */
export const NOTIFICATIE_ONTVANGER_BRONNEN = [
  /** Geen instelling: de standaard (bij een melding de doelgroep, bij een escalatie de eigenaar). */
  'STANDAARD',
  /** Een beheerder heeft de ontvangers ingesteld. */
  'OVERRIDE',
  /**
   * Ingesteld, maar niemand ervan is bereikbaar: de eigenaar neemt het over (of,
   * bij een smalle doelgroep zoals de betrokkene zelf, die doelgroep).
   */
  'TERUGVAL',
  /** Bewust niemand (bevestigd in het beheerscherm). */
  'NIEMAND',
  /** De doelgroep leverde niemand op: de eigenaar neemt het over. */
  'EIGENAAR',
  /** Geen ontvanger en geen eigenaar bereikbaar: de beheerders vangen het op. */
  'VANGNET',
] as const
export type NotificatieOntvangerBron = (typeof NOTIFICATIE_ONTVANGER_BRONNEN)[number]

export const NOTIFICATIE_ONTVANGER_BRON_LABELS: Record<NotificatieOntvangerBron, string> = {
  STANDAARD: 'Standaard (geen eigen instelling)',
  OVERRIDE: 'Ingestelde ontvangers',
  TERUGVAL: 'Ingestelde ontvangers niet bereikbaar',
  NIEMAND: 'Bewust niemand',
  EIGENAAR: 'Geen gerichte ontvanger, naar de eigenaar',
  VANGNET: 'Niemand bereikbaar, naar de beheerders',
}

/**
 * Via welk pad de melding het systeem verliet.
 *
 * `DIRECT` = een onderdeel (een cron, een route) stelde zelf de ontvangers vast
 * en leverde per persoon af, buiten de doelgroep-laag om. Daar valt niets in te
 * stellen (zie `NIET_INSTELBARE_EVENT_KEYS`) en een uitsluiting heeft er geen
 * effect; het spoor zegt dat eerlijk.
 */
export const NOTIFICATIE_LEVERPADEN = ['EVENT', 'NOTIFY', 'REGEL', 'ESCALATIE', 'DIRECT'] as const
export type NotificatieLeverpad = (typeof NOTIFICATIE_LEVERPADEN)[number]

/** Waarom DEZE ontvanger in de lijst zat. */
export const NOTIFICATIE_ONTVANGER_VIA = [
  /** De standaard doelgroep-resolver leverde hem op. */
  'RESOLVER',
  /** Per-persoon INCLUDE-uitzondering (spec 18). */
  'INCLUDE',
  /** FO-416-override op de melding: expliciet gekozen persoon. */
  'OVERRIDE_PERSOON',
  /** FO-416-override op de melding: lid van een gekozen rol. */
  'OVERRIDE_ROL',
  /** Ingesteld op de escalatie (`<regel>:escalatie`): expliciet gekozen persoon. */
  'ESCALATIE_PERSOON',
  /** Ingesteld op de escalatie (`<regel>:escalatie`): lid van een gekozen rol. */
  'ESCALATIE_ROL',
  /** Eigenaar: expliciet ingestelde eigenaar-persoon. */
  'EIGENAAR_PERSOON',
  /** Eigenaar: lid van de eigenaar-rol. */
  'EIGENAAR_ROL',
  /** Vangnet: beheerder, omdat er geen ontvanger en geen eigenaar bereikbaar was. */
  'BEHEER_VANGNET',
  /** `notify({ recipient: { role } })`: rol-brede verzending. */
  'ROL',
  /** `notify({ recipient: { userId | employeeId } })`: gerichte terugkoppeling. */
  'DIRECT',
  /** Test-fire vanuit de instellingen. */
  'TEST',
] as const
export type NotificatieOntvangerVia = (typeof NOTIFICATIE_ONTVANGER_VIA)[number]

/**
 * De `via`-waarden waarbij een BEHEERDER deze ontvanger met naam of via een
 * gekozen rol heeft ingesteld. Daar knipt een persoonlijke uitsluiting niet in:
 * de knop "Ik hoor dit niet te krijgen" seint dan alleen beheer.
 */
export const VIA_DOOR_BEHEER_INGESTELD: ReadonlySet<NotificatieOntvangerVia> = new Set([
  'INCLUDE',
  'OVERRIDE_PERSOON',
  'OVERRIDE_ROL',
  'ESCALATIE_PERSOON',
  'ESCALATIE_ROL',
  'EIGENAAR_PERSOON',
])

export interface NotificatieOntvangerBronDTO {
  pad: NotificatieLeverpad
  bron: NotificatieOntvangerBron
  via: NotificatieOntvangerVia
  /** Resolver-key van de standaard-doelgroep (bv. `administratie`). */
  resolver?: string | null
  eventKey?: string | null
  ruleCode?: string | null
  /** Id van de FO-416-override die de doelgroep bepaalde. */
  overrideId?: string | null
  /** De rol waarlangs de ontvanger binnenkwam (ROL, OVERRIDE_ROL, ESCALATIE_ROL, EIGENAAR_ROL). */
  rol?: string | null
  escalatieNiveau?: number | null
  /** Postvak-functie(s) van het gedeelde item dat voor dit event is aangemaakt (komma-gescheiden). */
  functie?: string | null
  /**
   * De melding is als verplicht of kritiek afgeleverd: een persoonlijke demping
   * van de regel geldt er niet voor. De knop "Ik hoor dit niet te krijgen"
   * belooft dan niets dat de levering niet waarmaakt.
   */
  kritiek?: boolean | null
  /**
   * De naam van de signalering (`NotificationRule.naam`) bij een regel-melding of
   * een escalatie. Wordt bij het LEZEN ingevuld door de api (een naam kan
   * wijzigen; het spoor bewaart de code), zodat een medewerker nooit een
   * regelcode als "FACTUUR_TE_LANG_OPEN" te zien krijgt.
   */
  regelNaam?: string | null
}

/**
 * Defensieve lezer voor de Json-kolom: een onverwachte vorm levert `null` op
 * in plaats van een crash in een lijst-route.
 */
export const notificatieOntvangerBronSchema = z.object({
  pad: z.enum(NOTIFICATIE_LEVERPADEN),
  bron: z.enum(NOTIFICATIE_ONTVANGER_BRONNEN),
  via: z.enum(NOTIFICATIE_ONTVANGER_VIA),
  resolver: z.string().nullable().optional(),
  eventKey: z.string().nullable().optional(),
  ruleCode: z.string().nullable().optional(),
  overrideId: z.string().nullable().optional(),
  rol: z.string().nullable().optional(),
  escalatieNiveau: z.number().int().nullable().optional(),
  functie: z.string().nullable().optional(),
  kritiek: z.boolean().nullable().optional(),
  regelNaam: z.string().nullable().optional(),
})

export function parseOntvangerBron(waarde: unknown): NotificatieOntvangerBronDTO | null {
  if (waarde === null || waarde === undefined) return null
  const r = notificatieOntvangerBronSchema.safeParse(waarde)
  return r.success ? r.data : null
}

const ROL_TEKST: Record<string, string> = {
  MONTEUR: 'de monteurs',
  TEAMLEIDER: 'de teamleiders',
  PLANNER: 'de planners',
  HR: 'HR',
  SALARIS: 'de salarisadministratie',
  MANAGER: 'het management',
  ADMIN: 'de beheerders',
}

function rolTekst(rol: string | null | undefined): string {
  if (!rol) return 'een rol'
  return ROL_TEKST[rol] ?? rol
}

/**
 * "De monteur op de werkbon" midden in een zin: het lidwoord klein. Een naam
 * zonder lidwoord ("Teamleider van de monteur", "HR + de nieuwe medewerker")
 * blijft staan, achter een dubbele punt.
 */
function gerichtAan(label: string): string {
  if (label.startsWith('De ')) return `aan de ${label.slice(3)}`
  if (label.startsWith('Het ')) return `aan het ${label.slice(4)}`
  return `aan: ${label}`
}

/** "A", "A en B", "A, B en C". */
function opsomming(delen: readonly string[]): string {
  if (delen.length <= 1) return delen[0] ?? ''
  return `${delen.slice(0, -1).join(', ')} en ${delen[delen.length - 1]}`
}

/**
 * Voor wie de zin geschreven is:
 *  - `ontvanger`: de ontvanger zelf ("je hoort bij ...") - de portal en de bel;
 *  - `beheer`: een beheerder die naar andermans melding kijkt ("hoort bij ...") -
 *    de Afleveringen-tab, de simulatie en het beheer-signaal.
 */
export type OntvangerBronPerspectief = 'ontvanger' | 'beheer'

export interface BeschrijfOntvangerBronOpties {
  /** Mens-leesbaar label van de doelgroep-resolver; zonder label de key. */
  resolverLabel?: string | null
  perspectief?: OntvangerBronPerspectief
  /** Mens-leesbaar label van een postvak-functie; zonder functie de key. */
  functieLabel?: (functie: string) => string
  /**
   * Wat voor doelgroep de resolver is (`resolverSoort` uit
   * notificatie-resolver-soort.ts). Bij PERSOON en GERICHT luidt de zin "deze
   * melding is gericht aan ..." in plaats van "je hoort bij de doelgroep ...".
   * Als letterlijke waarden, zodat deze module een bladmodule zonder imports blijft.
   */
  doelgroepSoort?: 'GROEP' | 'GERICHT' | 'PERSOON' | 'GESLOTEN_GROEP' | null
}

/**
 * PUUR - de "Waarom"-zin onder een melding, uit het bewaarde spoor.
 *
 * Geen "Waarom:"-voorvoegsel: het scherm plakt dat er zelf voor. Gebruik in de
 * apps `waaromZin()` uit `notificatie-waarom.ts`: die vult de labels in.
 */
export function beschrijfOntvangerBron(
  bron: NotificatieOntvangerBronDTO,
  opties: BeschrijfOntvangerBronOpties = {},
): string {
  const doelgroep = opties.resolverLabel ?? bron.resolver ?? 'de standaard-doelgroep'
  const zelf = (opties.perspectief ?? 'ontvanger') === 'ontvanger'
  const rol = rolTekst(bron.rol)
  const escalatie = bron.pad === 'ESCALATIE'
  // Een regel en zijn escalatie heten in de schermen "signalering".
  const soort = escalatie || bron.pad === 'REGEL' ? 'signalering' : 'meldingssoort'
  // Waarom de eigenaar (of het vangnet) aan de beurt kwam.
  const aanleiding =
    bron.bron === 'TERUGVAL'
      ? 'de ingestelde ontvangers waren niet bereikbaar'
      : escalatie && bron.bron === 'STANDAARD'
        ? 'voor deze escalatie zijn geen eigen ontvangers ingesteld'
        : 'er was geen gerichte ontvanger'

  let kern: string
  switch (bron.via) {
    case 'RESOLVER':
      // De betrokkene zelf of een aangewezen verantwoordelijke "hoort niet bij een
      // doelgroep": de melding is aan hem gericht. De zin is dan voor de ontvanger
      // en voor beheer dezelfde.
      kern =
        opties.doelgroepSoort === 'PERSOON' || opties.doelgroepSoort === 'GERICHT'
          ? `deze melding is gericht ${gerichtAan(doelgroep)}`
          : zelf
            ? `je hoort bij de doelgroep ${doelgroep}`
            : `hoort bij de doelgroep ${doelgroep}`
      // Een override die niemand bereikbaars meer aanwijst valt bij een smalle
      // doelgroep (de betrokkene zelf) terug op die doelgroep.
      if (bron.bron === 'TERUGVAL') kern += '; de ingestelde ontvangers waren niet bereikbaar'
      break
    case 'INCLUDE':
      kern = zelf
        ? 'een beheerder heeft je als extra ontvanger toegevoegd'
        : 'door een beheerder als extra ontvanger toegevoegd'
      break
    case 'OVERRIDE_PERSOON':
      kern = zelf
        ? 'een beheerder heeft je als ontvanger van deze melding ingesteld'
        : 'door een beheerder ingesteld als ontvanger van deze melding'
      break
    case 'OVERRIDE_ROL':
      kern = zelf
        ? `een beheerder heeft deze melding ingesteld voor ${rol}, en daar hoor jij bij`
        : `hoort bij ${rol}; een beheerder heeft deze melding voor die rol ingesteld`
      break
    case 'ESCALATIE_PERSOON':
      kern = zelf
        ? 'een beheerder heeft je als escalatie-ontvanger ingesteld'
        : 'door een beheerder ingesteld als escalatie-ontvanger'
      break
    case 'ESCALATIE_ROL':
      kern = zelf
        ? `een beheerder heeft de escalatie ingesteld voor ${rol}, en daar hoor jij bij`
        : `hoort bij ${rol}; een beheerder heeft de escalatie voor die rol ingesteld`
      break
    case 'EIGENAAR_PERSOON':
      kern = zelf
        ? `een beheerder heeft je als eigenaar van deze ${soort} ingesteld, en ${aanleiding}`
        : `door een beheerder ingesteld als eigenaar van deze ${soort}; ${aanleiding}`
      break
    case 'EIGENAAR_ROL':
      kern = zelf
        ? `je hoort bij ${rol}, de eigenaar van deze ${soort}, en ${aanleiding}`
        : `hoort bij ${rol}, de eigenaar van deze ${soort}; ${aanleiding}`
      break
    case 'BEHEER_VANGNET':
      kern = zelf
        ? `je bent beheerder; ${aanleiding} en ook de eigenaar van deze ${soort} was niet bereikbaar`
        : `beheerder; ${aanleiding} en ook de eigenaar van deze ${soort} was niet bereikbaar`
      break
    case 'ROL':
      kern = `deze melding is verstuurd aan ${rol}`
      break
    case 'DIRECT':
      if (bron.pad === 'DIRECT') {
        // Rechtstreekse aflevering door een onderdeel dat zelf de ontvangers bepaalt.
        kern = zelf
          ? 'het onderdeel dat deze melding verstuurt heeft jou als ontvanger bepaald'
          : 'door het versturende onderdeel als ontvanger bepaald (rechtstreekse aflevering, niet instelbaar)'
      } else {
        kern = zelf
          ? 'deze melding is rechtstreeks aan jou gericht'
          : 'rechtstreeks aan deze persoon gericht'
      }
      break
    case 'TEST':
      kern = 'testmelding vanuit de instellingen'
      break
    default:
      kern = 'onbekend'
  }

  // Welke signalering: met de naam als die bekend is. Een medewerker krijgt nooit
  // de code te zien; beheer zonder naam wel (dat is dan de enige aanwijzing).
  let voor = ''
  const naam = bron.regelNaam?.trim() ? `"${bron.regelNaam.trim()}"` : null
  if (escalatie) {
    const niveau = bron.escalatieNiveau ? ` niveau ${bron.escalatieNiveau}` : ''
    voor = naam
      ? `escalatie${niveau} van de signalering ${naam}: `
      : zelf
        ? `escalatie${niveau}: `
        : `escalatie${niveau} van regel ${bron.ruleCode ?? 'onbekend'}: `
  } else if (bron.pad === 'REGEL' && bron.ruleCode) {
    voor = naam ? `signalering ${naam}: ` : zelf ? '' : `regel ${bron.ruleCode}: `
  }

  const functieLabel = opties.functieLabel ?? ((f: string) => f)
  const functies = (bron.functie ?? '')
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean)
    .map(functieLabel)
  const functie =
    functies.length > 0 ? ` Ook als gedeeld postvak-item bij ${opsomming(functies)}.` : ''
  return `${voor}${kern}.${functie}`
}
